import CloseIcon from '@mui/icons-material/Close';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import React, { useState, useEffect, useRef } from 'react';

import { useCustomerProperties } from '../../hooks/useCustomerProperties';
import { customerLookupApi, PriceGroup, CustomerDiscountGroup } from '../../services/customerLookupApi';
import { getCustomerPropertyTypes, getPropertyValuesByType } from '../../services/customerPropertiesApi';
import { resolveLookupDefaults, resolveSalespersonByRegion } from '../../services/customerPropertiesDefaults';
import { salespersonApi, Salesperson } from '../../services/salespersonApi';
import { getCurrentUserDatabases, UserDatabaseDto } from '../../services/userDatabaseService';

interface CustomerPropertiesModalProps {
  open: boolean;
  onClose: () => void;
  customerCode: string;
  onSave?: (payload: {
    CustomerProperties: Record<string, string>;
    PerDbCustomerProperties: Record<string, Record<string, string>>;
    PerDbWholesalePriceGroupCode: Record<string, string>;
    PerDbDiscountGroupCode: Record<string, string>;
    PerDbSalespersonCodes: Record<string, string>;
  }) => void; 
  // Yeni: Lookup seçimleri değiştiğinde ebeveyne bildir
  onLookupSelectionChange?: (priceGroupCode: string, discountGroupCode: string) => void;
  // Gelişmiş: ST DERİ ve SERKAN ÇANTA için ayrı seçimler
  onSelectionChangePerDb?: (data: {
    stDeri: { priceGroup: string; discountGroup: string; invoiceType: string; salesperson?: string };
    serkanCanta: { priceGroup: string; discountGroup: string; invoiceType: string; salesperson?: string };
  }) => void;
  // Bölgeye göre ST DERİ temsilci auto-select
  regionCode?: string | null;
  // Modal tamamen yüklenince otomatik kaydet (bir kez)
  autoSaveOnReady?: boolean;
  // Otomatik kaydetmeden önce beklenecek süre (ms)
  autoSaveDelayMs?: number;
}

interface PropertyType {
  typeCode: number;
  typeName: string;
  valueType: string;
}

interface PropertyValue {
  code: string;
  name: string;
}

const CustomerPropertiesModal: React.FC<CustomerPropertiesModalProps> = ({ 
  open, 
  onClose, 
  customerCode,
  onSave,
  onLookupSelectionChange,
  onSelectionChangePerDb,
  regionCode,
  autoSaveOnReady,
  autoSaveDelayMs
}) => {
  const DEBUG = process.env.NODE_ENV !== 'production';
  const { propertyTypes, propertyValues, customerProperties, loading, error, saveProperties } = useCustomerProperties(customerCode !== "temp" ? customerCode : undefined);
  const [selectedProperties, setSelectedProperties] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  // DB Grupları
  const GROUPS = {
    ST_DERI: ["V3_StDeri", "V3_StDeriLTD"],
    SERKAN_CANTA: ["V3_SerkanCanta", "V3_SerkanCantaLTD"],
  } as const;
  // Lookup listeleri
  const [priceGroups, setPriceGroups] = useState<PriceGroup[]>([]);
  const [discountGroups, setDiscountGroups] = useState<CustomerDiscountGroup[]>([]);
  // ST DERİ seçimleri
  const [selectedPriceGroupSt, setSelectedPriceGroupSt] = useState<string>('');
  const [selectedDiscountGroupSt, setSelectedDiscountGroupSt] = useState<string>('');
  const [selectedInvoiceTypeSt, setSelectedInvoiceTypeSt] = useState<string>('');
  // SERKAN ÇANTA seçimleri
  const [selectedPriceGroupSc, setSelectedPriceGroupSc] = useState<string>('');
  const [selectedDiscountGroupSc, setSelectedDiscountGroupSc] = useState<string>('');
  const [selectedInvoiceTypeSc, setSelectedInvoiceTypeSc] = useState<string>('');
  // İki dataset için temsilci listeleri ve seçimleri
  const [salespersonsStDeri, setSalespersonsStDeri] = useState<Salesperson[]>([]);
  const [salespersonsSerkanCanta, setSalespersonsSerkanCanta] = useState<Salesperson[]>([]);
  const [selectedSalespersonStDeri, setSelectedSalespersonStDeri] = useState<string>('');
  const [selectedSalespersonSerkanCanta, setSelectedSalespersonSerkanCanta] = useState<string>('');
  // Bölge değiştiğinde otomatik atama yapıp yapmayacağımızı izlemek için bayrak
  const [isStDeriSalespersonAuto, setIsStDeriSalespersonAuto] = useState<boolean>(false);
  
  // Yeni state'ler ekleyelim
  const [localPropertyTypes, setLocalPropertyTypes] = useState<any[]>([]);
  const [localPropertyValues, setLocalPropertyValues] = useState<Record<number, any[]>>({});
  const [localLoading, setLocalLoading] = useState<boolean>(false);
  const [localError, setLocalError] = useState<Error | null>(null);
  // StrictMode veya yeniden renderlarda tekrarlı yüklemeyi engelle
  const hasLoadedRef = useRef<boolean>(false);
  // Otomatik kaydetme bir kez tetiklensin
  const autoSavedRef = useRef<boolean>(false);
  const autoSaveTimeoutRef = useRef<number | null>(null);
  // Otomatik kaydetme sırasında kapanmayı bastır
  const suppressCloseRef = useRef<boolean>(false);
  // Başlangıç (varsayılan) seçimlerin anlık görüntüsü (dirty kontrolü için)
  const initialSelectionsRef = useRef<{
    st: { price: string; disc: string; inv: string; sp: string };
    sc: { price: string; disc: string; inv: string; sp: string };
  } | null>(null);

  // Uygulanacak varsayılan değerler (isim eşleşmesine göre)
  const DEFAULT_VALUE_NAME_MATCHERS = [
    'Tam Faturalı',
    'Tümü',
    'United Colors Of Benetton - Matthew Cox - Sisley'
  ];

  // Bir özellik tipi için uygun varsayılanı bul
  const findDefaultValueForType = (values: any[] | undefined): string | '' => {
    if (!values || values.length === 0) return '';
    // Önce hedeflenen isimlere göre ara
    for (const matcher of DEFAULT_VALUE_NAME_MATCHERS) {
      const match = values.find(v =>
        (v.valueName || v.name || '').toLowerCase().includes(matcher.toLowerCase())
      );
      if (match) return match.valueCode || match.code || '';
    }
    // Bulunamazsa ilk değeri al
    const first = values[0];
    return first?.valueCode || first?.code || '';
  };

  // Fatura Şekli değerlerini bulmak için yardımcı
  const getInvoiceTypeValues = (): any[] | undefined => {
    const types = customerCode === 'temp' ? localPropertyTypes : propertyTypes;
    const valuesMap = customerCode === 'temp' ? localPropertyValues : propertyValues;
    const invoiceType = (types || []).find((t: any) => (t.typeName || '').toLowerCase().includes('fatura şekli'));
    if (!invoiceType) return undefined;
    return valuesMap[invoiceType.typeCode];
  };

  // Yardımcı: DB listesine aynı değeri yay
  function expandToDbs<T>(value: T, dbs: readonly string[]) {
    return (dbs as readonly string[]).reduce<Record<string, T>>((acc, db) => {
      acc[db] = value;
      return acc;
    }, {});
  }

  // Modal payload'ını oluştur
  function buildModalPayload() {
    // typeCode -> value
    const companies = selectedProperties[2]; // Çalıştığı Şirketler
    const brands = selectedProperties[3];    // Çalıştığı Markalar (çoklu ise virgüllü gelir)

    const customerPropertiesOut: Record<string, string> = {};
    if (companies !== undefined) customerPropertiesOut["2"] = String(companies || '');
    if (brands !== undefined) customerPropertiesOut["3"] = String(brands || '');

    // Fatura Şekli: per-DB (type 1)
    const perDbCustomerProps: Record<string, Record<string, string>> = {
      ...expandToDbs({ "1": selectedInvoiceTypeSt || '' }, GROUPS.ST_DERI),
      ...expandToDbs({ "1": selectedInvoiceTypeSc || '' }, GROUPS.SERKAN_CANTA),
    };

    const perDbWholesale: Record<string, string> = {
      ...expandToDbs(selectedPriceGroupSt || '', GROUPS.ST_DERI),
      ...expandToDbs(selectedPriceGroupSc || '', GROUPS.SERKAN_CANTA),
    };
    const perDbDiscount: Record<string, string> = {
      ...expandToDbs(selectedDiscountGroupSt || '', GROUPS.ST_DERI),
      ...expandToDbs(selectedDiscountGroupSc || '', GROUPS.SERKAN_CANTA),
    };
    const perDbSalespersons: Record<string, string> = {
      ...expandToDbs(selectedSalespersonStDeri || '', GROUPS.ST_DERI),
      ...expandToDbs(selectedSalespersonSerkanCanta || '', GROUPS.SERKAN_CANTA),
    };

    return {
      CustomerProperties: customerPropertiesOut,
      PerDbCustomerProperties: perDbCustomerProps,
      PerDbWholesalePriceGroupCode: perDbWholesale,
      PerDbDiscountGroupCode: perDbDiscount,
      PerDbSalespersonCodes: perDbSalespersons,
    };
  }

  // Özellikleri kaydet
  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveError(null);
      setSaveSuccess(false);

      // Zorunlu alan kontrolleri (Hesap İskonto Grubu hariç tüm modal alanları zorunlu)
      const missing: string[] = [];
      if (!selectedPriceGroupSt) missing.push('ST DERİ Toptan Satış Grubu');
      if (!selectedInvoiceTypeSt) missing.push('ST DERİ Fatura Şekli');
      if (!selectedSalespersonStDeri) missing.push('ST DERİ Temsilci');
      if (!selectedPriceGroupSc) missing.push('SERKAN ÇANTA Toptan Satış Grubu');
      if (!selectedInvoiceTypeSc) missing.push('SERKAN ÇANTA Fatura Şekli');
      if (!selectedSalespersonSerkanCanta) missing.push('SERKAN ÇANTA Temsilci');
      if (missing.length > 0) {
        setSaveError(`Zorunlu alan(lar) eksik: ${missing.join(', ')}`);
        return;
      }

      // Geçici müşteri ise parent'a payload dök
      if (customerCode === 'temp') {
        if (onSave) {
          const payload = buildModalPayload();
          if (DEBUG) console.log('[MODAL] Payload (temp kaydet):', payload);
          onSave(payload);
        }
        setSaveSuccess(true);
        // Manuel kayıtta kapat, otomatikte kapatma
        if (!suppressCloseRef.current) {
          setTimeout(() => {
            onClose();
          }, 500);
        } else {
          suppressCloseRef.current = false;
        }
        return;
      }

      // Normal (kalıcı) kaydetme işlemi
      const payload = buildModalPayload();
      if (DEBUG) console.log('[MODAL] Payload (kalıcı kaydet):', payload);
      await saveProperties(selectedProperties);

      setSaveSuccess(true);
      // Manuel kayıtta kapat, otomatikte kapatma
      if (!suppressCloseRef.current) {
        setTimeout(() => {
          onClose();
        }, 500);
      } else {
        suppressCloseRef.current = false;
      }
    } catch (error) {
      setSaveError((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  // Modal açıldığında özellik tiplerini ve değerlerini yükle
  useEffect(() => {
    if (!open) {
      hasLoadedRef.current = false; // modal kapandıysa bir sonraki açılış için sıfırla
      autoSavedRef.current = false; // otomatik kaydet bayrağını sıfırla
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
        autoSaveTimeoutRef.current = null;
      }
      suppressCloseRef.current = false;
      initialSelectionsRef.current = null;
      return;
    }
    // Lookup listelerini yükle
    (async () => {
      try {
        const [pg, dg] = await Promise.all([
          customerLookupApi.getPriceGroups(),
          customerLookupApi.getCustomerDiscountGroups()
        ]);
        setPriceGroups(pg || []);
        setDiscountGroups(dg || []);

        // Varsayılanlar: her iki dataset için TSF / 12 ve Fatura Şekli: 10 - Tam Faturalı
        const { priceGroupCode: preferredPriceCode, discountGroupCode: preferredDiscountCode } = resolveLookupDefaults();

        const pickPrice = (): string => {
          if (!pg || pg.length === 0) return '';
          const preferred = (preferredPriceCode || '').toUpperCase();
          const match = pg.find(x => (String(x.priceGroupCode || '')).toUpperCase() === preferred);
          return match ? (match.priceGroupCode || '') : (pg[0].priceGroupCode || '');
        };
        const pickDiscount = (): string => {
          // İSKONTO: Varsayılanı boş bırak (kullanıcı seçmezse boş kalsın)
          return '';
        };

        const invoiceValues = getInvoiceTypeValues();
        const pickInvoice = (): string => {
          if (!invoiceValues || invoiceValues.length === 0) return '';
          // Önce isimde "Tam Faturalı" veya kodu 10 olanı bul
          const byName = invoiceValues.find((v: any) => ((v.valueName || v.name || '') as string).toLowerCase().includes('tam fatural'));
          if (byName) return byName.valueCode || byName.code || '';
          const byCode10 = invoiceValues.find((v: any) => String(v.valueCode || v.code) === '10');
          if (byCode10) return byCode10.valueCode || byCode10.code || '';
          const first = invoiceValues[0];
          return first?.valueCode || first?.code || '';
        };

        const initPrice = pickPrice();
        const initDiscount = pickDiscount();
        const initInvoice = pickInvoice();

        setSelectedPriceGroupSt(prev => prev || initPrice);
        setSelectedDiscountGroupSt(prev => (prev !== undefined ? prev : initDiscount));
        setSelectedInvoiceTypeSt(prev => prev || initInvoice);
        setSelectedPriceGroupSc(prev => prev || initPrice);
        setSelectedDiscountGroupSc(prev => (prev !== undefined ? prev : initDiscount));
        setSelectedInvoiceTypeSc(prev => prev || initInvoice);

        // Gelişmiş callback (per-DB) varsa onu kullan; varsa eski global callback'i çağırma
        if (typeof onSelectionChangePerDb === 'function') {
          onSelectionChangePerDb({
            stDeri: { priceGroup: initPrice, discountGroup: initDiscount, invoiceType: initInvoice, salesperson: selectedSalespersonStDeri },
            serkanCanta: { priceGroup: initPrice, discountGroup: initDiscount, invoiceType: initInvoice, salesperson: selectedSalespersonSerkanCanta }
          });
        } else if (onLookupSelectionChange) {
          // geri uyumluluk: per-DB yoksa global callback'i tetikle
          onLookupSelectionChange(initPrice, initDiscount);
        }
      } catch (e) {
        if (DEBUG) console.error('Lookup listeleri yüklenemedi', e);
      }
    })();
    // İki dataset için temsilci listelerini yükle (varsayılan: boş seçim)
    (async () => {
      try {
        const dbs: UserDatabaseDto[] = await getCurrentUserDatabases();
        // QR modunda dbs boş ise global fallback kullan
        if (!dbs || dbs.length === 0) {
          if (DEBUG) console.log('QR modu veya DB bilinmiyor: Global satış temsilcileri yükleniyor (fallback)');
          const globalList = await salespersonApi.getSalespersons();
          setSalespersonsStDeri(globalList || []);
          setSalespersonsSerkanCanta(globalList || []);
          // Varsayılan seçimler (mevcut kurallarla uyumlu)
          setSelectedSalespersonStDeri(prev => {
            if (prev) return prev;
            const tsf = (globalList || []).find(x => (x.salespersonCode || '').toUpperCase() === 'TSF');
            const val = tsf ? tsf.salespersonCode : '';
            if (val) setIsStDeriSalespersonAuto(true);
            return val;
          });
          setSelectedSalespersonSerkanCanta(prev => {
            if (prev) return prev;
            const d204 = (globalList || []).find(x => (x.salespersonCode || '').toString() === '204');
            return d204 ? d204.salespersonCode : '';
          });
          return;
        }

        // İsimlere göre bul (normal JWT modu)
        const st = dbs.find(d => (d.databaseName || '').toLowerCase() === 'v3_stderi');
        const sc = dbs.find(d => (d.databaseName || '').toLowerCase() === 'v3_serkancanta');

        // ST DERİ listesi: DB bulunamazsa global fallback kullan
        let stList: Salesperson[] = [];
        if (st?.databaseId) {
          stList = await salespersonApi.getSalespersonsByDatabaseId(st.databaseId);
        } else {
          stList = await salespersonApi.getSalespersons();
        }
        setSalespersonsStDeri(stList || []);

        // SERKAN ÇANTA listesi: DB bulunamazsa global fallback kullan
        let scList: Salesperson[] = [];
        if (sc?.databaseId) {
          scList = await salespersonApi.getSalespersonsByDatabaseId(sc.databaseId);
        } else {
          scList = await salespersonApi.getSalespersons();
        }
        setSalespersonsSerkanCanta(scList || []);
        // ST DERİ için varsayılan: TSF varsa otomatik seç
        setSelectedSalespersonStDeri(prev => {
          if (prev) return prev;
          const tsf = (stList || []).find(x => (x.salespersonCode || '').toUpperCase() === 'TSF');
          const val = tsf ? tsf.salespersonCode : '';
          if (val) setIsStDeriSalespersonAuto(true);
          return val;
        });
        setSelectedSalespersonSerkanCanta(prev => prev || '');
      } catch (e) {
        if (DEBUG) console.error('Temsilci listeleri yüklenemedi', e);
        setSalespersonsStDeri([]);
        setSalespersonsSerkanCanta([]);
      }
    })();
    // Eğer temp müşteri ise veya propertyTypes boşsa, manuel olarak yükle
    if ((customerCode === "temp" || propertyTypes.length === 0) && !hasLoadedRef.current) {
      if (DEBUG) console.log('Özellik tipleri ve değerleri manuel olarak yükleniyor...');
      hasLoadedRef.current = true; // aynı açılış döngüsünde ikinci kez tetiklenmesin
      loadLocalProperties();
    }
  }, [open, customerCode, propertyTypes.length]);

  // Modal hazır olduğunda bir kere otomatik kaydet
  useEffect(() => {
    if (!open) return;
    if (!autoSaveOnReady) return;
    // Yükleme veya hata varsa bekle
    const isLoading = customerCode === "temp" ? localLoading : loading;
    const errObj = customerCode === "temp" ? localError : error;
    const hasError = errObj !== null && errObj !== undefined;
    if (isLoading || hasError) return;
    // Zorunlu alanlar dolu mu?
    const requiredFilled =
      !!selectedPriceGroupSt &&
      !!selectedInvoiceTypeSt &&
      !!selectedSalespersonStDeri &&
      !!selectedPriceGroupSc &&
      !!selectedInvoiceTypeSc &&
      !!selectedSalespersonSerkanCanta;
    if (!requiredFilled) return;
    // Başlangıç snapshot'ını bir kez al
    if (!initialSelectionsRef.current) {
      initialSelectionsRef.current = {
        st: { price: selectedPriceGroupSt, disc: selectedDiscountGroupSt, inv: selectedInvoiceTypeSt, sp: selectedSalespersonStDeri },
        sc: { price: selectedPriceGroupSc, disc: selectedDiscountGroupSc, inv: selectedInvoiceTypeSc, sp: selectedSalespersonSerkanCanta },
      };
    }
    if (saving) return;
    if (autoSavedRef.current) return;
    // Gecikmeli otomatik kaydet
    const delay = typeof autoSaveDelayMs === 'number' ? autoSaveDelayMs : 2000;
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
    autoSaveTimeoutRef.current = window.setTimeout(() => {
      if (autoSavedRef.current) return;
      autoSavedRef.current = true;
      // Otomatik kaydet: kapanmayı engelle
      suppressCloseRef.current = true;
      handleSave();
    }, delay);
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
        autoSaveTimeoutRef.current = null;
      }
    };
  }, [open, autoSaveOnReady, autoSaveDelayMs, customerCode, localLoading, loading, localError, error, selectedPriceGroupSt, selectedInvoiceTypeSt, selectedSalespersonStDeri, selectedPriceGroupSc, selectedInvoiceTypeSc, selectedSalespersonSerkanCanta, saving]);

  // Bölgeye göre ST DERİ temsilci auto-select
  useEffect(() => {
    if (!open) return;
    const region = (regionCode || '').trim();
    if (!region) return;
    // Liste yüklü olmalı ve mevcut seçim boş olmalı
    if (!salespersonsStDeri || salespersonsStDeri.length === 0) return;
    // Kullanıcı manuel seçim yaptıysa otomatik atama yapma; sadece auto veya boşsa güncelle
    const resolved = resolveSalespersonByRegion(region);
    if (!resolved) return;
    const exists = salespersonsStDeri.some(x => (x.salespersonCode || '').toString() === resolved);
    if (!exists) return;
    if ((isStDeriSalespersonAuto || !selectedSalespersonStDeri) && resolved !== selectedSalespersonStDeri) {
      setSelectedSalespersonStDeri(resolved);
      setIsStDeriSalespersonAuto(true);
      if (typeof onSelectionChangePerDb === 'function') {
        onSelectionChangePerDb({
          stDeri: { priceGroup: selectedPriceGroupSt, discountGroup: selectedDiscountGroupSt, invoiceType: selectedInvoiceTypeSt, salesperson: resolved },
          serkanCanta: { priceGroup: selectedPriceGroupSc, discountGroup: selectedDiscountGroupSc, invoiceType: selectedInvoiceTypeSc, salesperson: selectedSalespersonSerkanCanta }
        });
      }
    }
  }, [open, regionCode, salespersonsStDeri, selectedSalespersonStDeri, selectedPriceGroupSt, selectedDiscountGroupSt, selectedInvoiceTypeSt, selectedPriceGroupSc, selectedDiscountGroupSc, selectedInvoiceTypeSc, selectedSalespersonSerkanCanta, onSelectionChangePerDb, isStDeriSalespersonAuto]);

  // SERKAN ÇANTA tarafı: default temsilci (204 - Mağaza) otomatik seç (aktif DB'den bağımsız)
  useEffect(() => {
    if (!open) return;
    if (!salespersonsSerkanCanta || salespersonsSerkanCanta.length === 0) return;
    if (selectedSalespersonSerkanCanta) return;
    const def = '204';
    const exists = salespersonsSerkanCanta.some(x => (x.salespersonCode || '').toString() === def);
    if (!exists) return;
    setSelectedSalespersonSerkanCanta(def);
    if (typeof onSelectionChangePerDb === 'function') {
      onSelectionChangePerDb({
        stDeri: { priceGroup: selectedPriceGroupSt, discountGroup: selectedDiscountGroupSt, invoiceType: selectedInvoiceTypeSt, salesperson: selectedSalespersonStDeri },
        serkanCanta: { priceGroup: selectedPriceGroupSc, discountGroup: selectedDiscountGroupSc, invoiceType: selectedInvoiceTypeSc, salesperson: def }
      });
    }
  }, [open, salespersonsSerkanCanta, selectedSalespersonSerkanCanta, selectedPriceGroupSt, selectedDiscountGroupSt, selectedInvoiceTypeSt, selectedPriceGroupSc, selectedDiscountGroupSc, selectedInvoiceTypeSc, selectedSalespersonStDeri, onSelectionChangePerDb]);

  // Tüm özellik tiplerini ve değerlerini manuel olarak yükle
  const loadLocalProperties = async () => {
    // Eğer zaten yükleme yapılıyorsa veya özellik tipleri zaten yüklenmişse, işlemi tekrarlama
    if (localLoading || localPropertyTypes.length > 0) return false;
    
    try {
      setLocalLoading(true);
      setLocalError(null);
      
      // Özellik tiplerini yükle
      const typesData = await getCustomerPropertyTypes();
      if (DEBUG) console.log('Manuel yüklenen özellik tipleri:', typesData);
      
      // Eğer component unmount olduysa işlemi durdur
      if (!typesData || typesData.length === 0) {
        if (DEBUG) console.log('Özellik tipi bulunamadı veya component unmount oldu');
        return false;
      }
      
      setLocalPropertyTypes(typesData);
      
      // Her tip için değerleri yükle
      const valuesObj: Record<number, any[]> = {};
      for (const type of typesData) {
        const valuesData = await getPropertyValuesByType(type.typeCode);
        if (DEBUG) console.log(`Manuel yüklenen ${type.typeCode} tipindeki değerler:`, valuesData);
        valuesObj[type.typeCode] = valuesData;
      }
      
      setLocalPropertyValues(valuesObj);
      // Eğer seçili özellik yoksa, varsayılanları uygula
      if (Object.keys(selectedProperties).length === 0) {
        const defaults: Record<number, string> = {};
        typesData.forEach((t: any) => {
          defaults[t.typeCode] = findDefaultValueForType(valuesObj[t.typeCode]);
        });
        setSelectedProperties(defaults);
      }
      return true;
    } catch (err) {
      setLocalError(err as Error);
      if (DEBUG) console.error('Müşteri özellikleri manuel yüklenirken hata:', err);
      return false;
    } finally {
      setLocalLoading(false);
    }
  };

  // Müşteri özellikleri değiştiğinde seçili özellikleri güncelle
  useEffect(() => {
    if (customerProperties) {
      setSelectedProperties(customerProperties);
    }
  }, [customerProperties]);

  // API'den gelen propertyTypes/propertyValues ile varsayılanları uygula (temp olmayan müşteriler için)
  useEffect(() => {
    if (!open) return;
    if (customerCode === 'temp') return; // temp senaryosu local loader üzerinden hallediliyor
    if (!propertyTypes || propertyTypes.length === 0) return;
    if (!propertyValues || Object.keys(propertyValues).length === 0) return;
    if (Object.keys(selectedProperties).length > 0) return; // zaten seçili varsa dokunma

    const defaults: Record<number, string> = {};
    propertyTypes.forEach((t: any) => {
      const values = propertyValues[t.typeCode];
      defaults[t.typeCode] = findDefaultValueForType(values);
    });
    if (Object.keys(defaults).length > 0) {
      setSelectedProperties(defaults);
    }
  }, [open, customerCode, propertyTypes, propertyValues]);

  // Fatura Şekli: local/API değerleri yüklendikten sonra '10 - Tam Faturalı' seçili değilse seç
  useEffect(() => {
    if (!open) return;
    const invoiceValues = getInvoiceTypeValues();
    if (!invoiceValues || invoiceValues.length === 0) return;
    const pickInvoice = (): string => {
      const byName = invoiceValues.find((v: any) => ((v.valueName || v.name || '') as string).toLowerCase().includes('tam fatural'));
      if (byName) return byName.valueCode || byName.code || '';
      const byCode10 = invoiceValues.find((v: any) => String(v.valueCode || v.code) === '10');
      if (byCode10) return byCode10.valueCode || byCode10.code || '';
      const first = invoiceValues[0];
      return first?.valueCode || first?.code || '';
    };
    let updated = false;
    setSelectedInvoiceTypeSt(prev => {
      if (!prev) { updated = true; return pickInvoice(); }
      return prev;
    });
    setSelectedInvoiceTypeSc(prev => {
      if (!prev) { updated = true; return pickInvoice(); }
      return prev;
    });
    // Varsayılan set edildiyse, ebeveyne güncel bilgileri gönder
    if (updated && typeof onSelectionChangePerDb === 'function') {
      const pgSt = selectedPriceGroupSt;
      const dgSt = selectedDiscountGroupSt;
      const pgSc = selectedPriceGroupSc;
      const dgSc = selectedDiscountGroupSc;
      const invSt = selectedInvoiceTypeSt || pickInvoice();
      const invSc = selectedInvoiceTypeSc || pickInvoice();
      onSelectionChangePerDb({
        stDeri: { priceGroup: pgSt, discountGroup: dgSt, invoiceType: invSt, salesperson: selectedSalespersonStDeri },
        serkanCanta: { priceGroup: pgSc, discountGroup: dgSc, invoiceType: invSc, salesperson: selectedSalespersonSerkanCanta }
      });
    }
  }, [open, localPropertyValues, propertyValues]);

  // Özellik değişikliğini işle
  const handlePropertyChange = (propertyId: number, value: string) => {
    setSelectedProperties((prev: Record<number, string>) => ({
      ...prev,
      [propertyId]: value,
    }));
  };

  // Özellik tipine göre form elemanı render et
  const renderPropertyInput = (property: PropertyType) => {
    const value = selectedProperties[property.typeCode] || '';
    const isTemp = customerCode === "temp";
    const currentPropertyValues = isTemp ? localPropertyValues[property.typeCode] : propertyValues[property.typeCode];

    // Özel durum: "Eski Kod" alanını artık kullanmıyoruz -> tamamen gizle
    if (property.typeName === 'Eski Kod') {
      return null;
    }

    // Diğer özellikler için select kullan
    return (
      <FormControl fullWidth variant="outlined">
        <InputLabel 
          id={`property-${property.typeCode}-label`} 
          shrink
          sx={{ 
            backgroundColor: 'white', 
            padding: '0 5px',
            '&.Mui-focused': { color: 'primary.main' },
            zIndex: 1,
            fontWeight: 'medium',
            opacity: 1,
            color: 'rgba(0, 0, 0, 0.87)'
          }}
        >
          {property.typeName}
        </InputLabel>
        <Select
          labelId={`property-${property.typeCode}-label`}
          id={`property-${property.typeCode}`}
          value={value || ''}
          label={property.typeName}
          defaultValue=""
          displayEmpty
          onChange={(e) => handlePropertyChange(property.typeCode, e.target.value as string)}
          sx={{
            '& .MuiSelect-select': {
              padding: '12px 14px',
              backgroundColor: 'white',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }
          }}
          renderValue={(selected) => {
            if (!selected) return 'Seçiniz';
            const selectedOption = currentPropertyValues?.find(
              (option: any) => (option.valueCode || option.code) === selected
            );
            if (!selectedOption) return selected as string;
            const code = selectedOption.valueCode || selectedOption.code;
            const name = selectedOption.valueName || selectedOption.name;
            return `${code} - ${name}`;
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 300,
                minWidth: '100%',
                maxWidth: { xs: 'calc(100vw - 32px)', sm: 480, md: 560 },
                overflowX: 'hidden',
                marginLeft: 0,
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                mt: 1
              }
            },
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
            marginThreshold: 8,
            disableScrollLock: false
          }}
        >
          <MenuItem key="empty" value="">
            <em>Seçiniz</em>
          </MenuItem>
          {currentPropertyValues?.map((option: any, index: number) => (
            <MenuItem 
              key={`${property.typeCode}-${option.valueCode || option.code}-${index}`} 
              value={option.valueCode || option.code}
              sx={{
                py: 0.5,
                minHeight: 'auto',
                borderBottom: '1px solid rgba(0,0,0,0.05)',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
              }}
            >
              <Box display="flex" alignItems="center" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, whiteSpace: 'normal', wordBreak: 'break-word', width: '100%', padding: '4px 0', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                <Typography variant="body2" sx={{ fontSize: 'inherit', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.1, my: 0 }}>
                  <Box component="span" sx={{ fontWeight: 'bold', color: 'primary.main', mr: 0.5 }}>
                    {(option.valueCode || option.code)}
                  </Box>
                  - {option.valueName || option.name}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  // Yükleme durumunu belirle
  const isLoading = customerCode === "temp" ? localLoading : loading;
  const currentError = customerCode === "temp" ? localError : error;
  const currentPropertyTypes = customerCode === "temp" ? localPropertyTypes : propertyTypes;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Müşteri Özellikleri
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        {/* Ortak blok */}
        <Box sx={{ border: '1px solid', borderColor: 'grey.300', borderLeftWidth: 4, borderLeftColor: 'grey.500', borderRadius: 1, p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Tüm Veritabanları İçin Ortak</Typography>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="120px" sx={{ mb: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : currentError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Müşteri özellikleri yüklenirken bir hata oluştu: {currentError.message}
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            {currentPropertyTypes
              .filter((property: PropertyType) => {
                const name = (property.typeName || '').toLowerCase();
                // Sadece Şirketler/Markalar (ortak) göster
                const isCompany = name.includes('şirket');
                const isBrand = name.includes('marka');
                const isOldCode = name.includes('eski kod');
                return (isCompany || isBrand) && !isOldCode;
              })
              .map((property: PropertyType) => (
                <Box key={property.typeCode} sx={{ mb: 1 }}>
                  {renderPropertyInput(property)}
                </Box>
              ))}
          </Box>
        )}
        </Box>

        {/* ST DERİ için Fiyat/İskonto/Fatura ve Temsilci */}
        <Box sx={{ border: '1px solid', borderColor: 'primary.light', borderLeftWidth: 4, borderLeftColor: 'primary.main', borderRadius: 1, p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>ST DERİ</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 280px' }, minWidth: { xs: '100%', sm: 260 } }}>
            <FormControl fullWidth size="small" error={!selectedPriceGroupSt}>
              <InputLabel id="st-price-group-label">Toptan Satış Grubu</InputLabel>
              <Select
                labelId="st-price-group-label"
                value={selectedPriceGroupSt}
                label="Toptan Satış Grubu"
                onChange={(e) => {
                  const val = e.target.value as string;
                  setSelectedPriceGroupSt(val);
                  if (onSelectionChangePerDb) {
                    onSelectionChangePerDb({
                      stDeri: { priceGroup: val, discountGroup: selectedDiscountGroupSt, invoiceType: selectedInvoiceTypeSt, salesperson: selectedSalespersonStDeri },
                      serkanCanta: { priceGroup: selectedPriceGroupSc, discountGroup: selectedDiscountGroupSc, invoiceType: selectedInvoiceTypeSc, salesperson: selectedSalespersonSerkanCanta }
                    });
                  } else if (onLookupSelectionChange) {
                    onLookupSelectionChange(val, selectedDiscountGroupSt);
                  }
                }}
              >
                {priceGroups.map(pg => (
                  <MenuItem key={pg.priceGroupCode} value={pg.priceGroupCode}>
                    {pg.priceGroupCode} - {pg.priceGroupDescription}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 280px' }, minWidth: { xs: '100%', sm: 260 } }}>
            <FormControl fullWidth size="small" /* İsteğe bağlı alan, error yok */>
              <InputLabel 
                id="st-discount-group-label"
                shrink
                sx={{ 
                  backgroundColor: 'white', 
                  padding: '0 5px',
                  '&.Mui-focused': { color: 'primary.main' },
                  zIndex: 1,
                  fontWeight: 'medium',
                  opacity: 1,
                  color: 'rgba(0, 0, 0, 0.87)'
                }}
              >
                Hesap İskonto Grubu
              </InputLabel>
              <Select
                labelId="st-discount-group-label"
                value={selectedDiscountGroupSt}
                label="Hesap İskonto Grubu"
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) return 'Seçiniz';
                  const dg = discountGroups.find(x => x.discountGroupCode === selected);
                  if (!dg) return selected as string;
                  return `${dg.discountGroupCode} - ${dg.discountGroupDescription}`;
                }}
                onChange={(e) => {
                  const val = e.target.value as string;
                  setSelectedDiscountGroupSt(val);
                  if (onSelectionChangePerDb) {
                    onSelectionChangePerDb({
                      stDeri: { priceGroup: selectedPriceGroupSt, discountGroup: val, invoiceType: selectedInvoiceTypeSt, salesperson: selectedSalespersonStDeri },
                      serkanCanta: { priceGroup: selectedPriceGroupSc, discountGroup: selectedDiscountGroupSc, invoiceType: selectedInvoiceTypeSc, salesperson: selectedSalespersonSerkanCanta }
                    });
                  } else if (onLookupSelectionChange) {
                    onLookupSelectionChange(selectedPriceGroupSt, val);
                  }
                }}
              >
                <MenuItem value=""><em>Seçiniz</em></MenuItem>
                {discountGroups.map(dg => (
                  <MenuItem key={dg.discountGroupCode} value={dg.discountGroupCode}>
                    {dg.discountGroupCode} - {dg.discountGroupDescription}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 280px' }, minWidth: { xs: '100%', sm: 260 } }}>
            <FormControl fullWidth size="small" error={!selectedInvoiceTypeSt}>
              <InputLabel id="st-invoice-type-label">Fatura Şekli</InputLabel>
              <Select
                labelId="st-invoice-type-label"
                value={selectedInvoiceTypeSt}
                label="Fatura Şekli"
                onChange={(e) => {
                  const val = e.target.value as string;
                  setSelectedInvoiceTypeSt(val);
                  if (onSelectionChangePerDb) {
                    onSelectionChangePerDb({
                      stDeri: { priceGroup: selectedPriceGroupSt, discountGroup: selectedDiscountGroupSt, invoiceType: val, salesperson: selectedSalespersonStDeri },
                      serkanCanta: { priceGroup: selectedPriceGroupSc, discountGroup: selectedDiscountGroupSc, invoiceType: selectedInvoiceTypeSc, salesperson: selectedSalespersonSerkanCanta }
                    });
                  }
                }}
              >
                {(getInvoiceTypeValues() || []).map((it: any, idx: number) => (
                  <MenuItem key={`st-inv-${it.valueCode || it.code}-${idx}`} value={it.valueCode || it.code}>
                    {(it.valueCode || it.code)} - {(it.valueName || it.name)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {/* ST DERİ Temsilci: Fatura Şekli alanının hemen yanında (mobilde altında) */}
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 320px' }, minWidth: { xs: '100%', sm: 280 } }}>
            <FormControl fullWidth size="small" error={!selectedSalespersonStDeri}>
              <InputLabel 
                id="salesperson-st-deri-label"
                shrink
                sx={{ 
                  backgroundColor: 'white', 
                  padding: '0 5px',
                  '&.Mui-focused': { color: 'primary.main' },
                  zIndex: 1,
                  fontWeight: 'medium',
                  opacity: 1,
                  color: 'rgba(0, 0, 0, 0.87)'
                }}
              >
                ST DERİ Temsilci
              </InputLabel>
              <Select
                labelId="salesperson-st-deri-label"
                value={selectedSalespersonStDeri}
                label="ST DERİ Temsilci"
                onChange={(e) => {
                  const val = e.target.value as string;
                  setSelectedSalespersonStDeri(val);
                  setIsStDeriSalespersonAuto(false);
                  if (onSelectionChangePerDb) {
                    onSelectionChangePerDb({
                      stDeri: { priceGroup: selectedPriceGroupSt, discountGroup: selectedDiscountGroupSt, invoiceType: selectedInvoiceTypeSt, salesperson: val },
                      serkanCanta: { priceGroup: selectedPriceGroupSc, discountGroup: selectedDiscountGroupSc, invoiceType: selectedInvoiceTypeSc, salesperson: selectedSalespersonSerkanCanta }
                    });
                  }
                }}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) return 'Seçiniz';
                  const sp = salespersonsStDeri.find(x => x.salespersonCode === selected);
                  if (!sp) return selected as string;
                  return `${sp.salespersonCode} - ${sp.salespersonFirstLastName}`;
                }}
              >
                <MenuItem value=""><em>Seçiniz</em></MenuItem>
                {salespersonsStDeri.map(sp => (
                  <MenuItem key={sp.salespersonCode} value={sp.salespersonCode}>
                    {sp.salespersonCode} - {sp.salespersonFirstLastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
        </Box>
        {/* SERKAN ÇANTA için Fiyat/İskonto/Fatura */}
        <Box sx={{ border: '1px solid', borderColor: 'error.light', borderLeftWidth: 4, borderLeftColor: 'error.main', borderRadius: 1, p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>SERKAN ÇANTA</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 280px' }, minWidth: { xs: '100%', sm: 260 } }}>
            <FormControl fullWidth size="small" error={!selectedPriceGroupSc}>
              <InputLabel id="sc-price-group-label">Toptan Satış Grubu</InputLabel>
              <Select
                labelId="sc-price-group-label"
                value={selectedPriceGroupSc}
                label="Toptan Satış Grubu"
                onChange={(e) => {
                  const val = e.target.value as string;
                  setSelectedPriceGroupSc(val);
                  if (onSelectionChangePerDb) {
                    onSelectionChangePerDb({
                      stDeri: { priceGroup: selectedPriceGroupSt, discountGroup: selectedDiscountGroupSt, invoiceType: selectedInvoiceTypeSt, salesperson: selectedSalespersonStDeri },
                      serkanCanta: { priceGroup: val, discountGroup: selectedDiscountGroupSc, invoiceType: selectedInvoiceTypeSc, salesperson: selectedSalespersonSerkanCanta }
                    });
                  } else if (onLookupSelectionChange) {
                    onLookupSelectionChange(val, selectedDiscountGroupSc);
                  }
                }}
              >
                {priceGroups.map(pg => (
                  <MenuItem key={pg.priceGroupCode} value={pg.priceGroupCode}>
                    {pg.priceGroupCode} - {pg.priceGroupDescription}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 280px' }, minWidth: { xs: '100%', sm: 260 } }}>
            <FormControl fullWidth size="small" /* İsteğe bağlı alan, error yok */>
              <InputLabel 
                id="sc-discount-group-label"
                shrink
                sx={{ 
                  backgroundColor: 'white', 
                  padding: '0 5px',
                  '&.Mui-focused': { color: 'primary.main' },
                  zIndex: 1,
                  fontWeight: 'medium',
                  opacity: 1,
                  color: 'rgba(0, 0, 0, 0.87)'
                }}
              >
                Hesap İskonto Grubu
              </InputLabel>
              <Select
                labelId="sc-discount-group-label"
                value={selectedDiscountGroupSc}
                label="Hesap İskonto Grubu"
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) return 'Seçiniz';
                  const dg = discountGroups.find(x => x.discountGroupCode === selected);
                  if (!dg) return selected as string;
                  return `${dg.discountGroupCode} - ${dg.discountGroupDescription}`;
                }}
                onChange={(e) => {
                  const val = e.target.value as string;
                  setSelectedDiscountGroupSc(val);
                  if (onSelectionChangePerDb) {
                    onSelectionChangePerDb({
                      stDeri: { priceGroup: selectedPriceGroupSt, discountGroup: selectedDiscountGroupSt, invoiceType: selectedInvoiceTypeSt, salesperson: selectedSalespersonStDeri },
                      serkanCanta: { priceGroup: selectedPriceGroupSc, discountGroup: val, invoiceType: selectedInvoiceTypeSc, salesperson: selectedSalespersonSerkanCanta }
                    });
                  } else if (onLookupSelectionChange) {
                    onLookupSelectionChange(selectedPriceGroupSc, val);
                  }
                }}
              >
                <MenuItem value=""><em>Seçiniz</em></MenuItem>
                {discountGroups.map(dg => (
                  <MenuItem key={dg.discountGroupCode} value={dg.discountGroupCode}>
                    {dg.discountGroupCode} - {dg.discountGroupDescription}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 280px' }, minWidth: { xs: '100%', sm: 260 } }}>
            <FormControl fullWidth size="small" error={!selectedInvoiceTypeSc}>
              <InputLabel id="sc-invoice-type-label">Fatura Şekli</InputLabel>
              <Select
                labelId="sc-invoice-type-label"
                value={selectedInvoiceTypeSc}
                label="Fatura Şekli"
                onChange={(e) => {
                  const val = e.target.value as string;
                  setSelectedInvoiceTypeSc(val);
                  if (onSelectionChangePerDb) {
                    onSelectionChangePerDb({
                      stDeri: { priceGroup: selectedPriceGroupSt, discountGroup: selectedDiscountGroupSt, invoiceType: selectedInvoiceTypeSt, salesperson: selectedSalespersonStDeri },
                      serkanCanta: { priceGroup: selectedPriceGroupSc, discountGroup: selectedDiscountGroupSc, invoiceType: val, salesperson: selectedSalespersonSerkanCanta }
                    });
                  }
                }}
              >
                {(getInvoiceTypeValues() || []).map((it: any, idx: number) => (
                  <MenuItem key={`sc-inv-${it.valueCode || it.code}-${idx}`} value={it.valueCode || it.code}>
                    {(it.valueCode || it.code)} - {(it.valueName || it.name)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {/* SERKAN ÇANTA Temsilci: Fatura Şekli alanının hemen yanında (mobilde altında) */}
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 320px' }, minWidth: { xs: '100%', sm: 280 } }}>
            <FormControl fullWidth size="small" error={!selectedSalespersonSerkanCanta}>
              <InputLabel 
                id="salesperson-serkan-canta-label"
                shrink
                sx={{ 
                  backgroundColor: 'white', 
                  padding: '0 5px',
                  '&.Mui-focused': { color: 'primary.main' },
                  zIndex: 1,
                  fontWeight: 'medium',
                  opacity: 1,
                  color: 'rgba(0, 0, 0, 0.87)'
                }}
              >
                SERKAN ÇANTA Temsilci
              </InputLabel>
              <Select
                labelId="salesperson-serkan-canta-label"
                value={selectedSalespersonSerkanCanta}
                label="SERKAN ÇANTA Temsilci"
                onChange={(e) => {
                  const val = e.target.value as string;
                  setSelectedSalespersonSerkanCanta(val);
                  if (onSelectionChangePerDb) {
                    onSelectionChangePerDb({
                      stDeri: { priceGroup: selectedPriceGroupSt, discountGroup: selectedDiscountGroupSt, invoiceType: selectedInvoiceTypeSt, salesperson: selectedSalespersonStDeri },
                      serkanCanta: { priceGroup: selectedPriceGroupSc, discountGroup: selectedDiscountGroupSc, invoiceType: selectedInvoiceTypeSc, salesperson: val }
                    });
                  }
                }}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) return 'Seçiniz';
                  const sp = salespersonsSerkanCanta.find(x => x.salespersonCode === selected);
                  if (!sp) return selected as string;
                  return `${sp.salespersonCode} - ${sp.salespersonFirstLastName}`;
                }}
              >
                <MenuItem value=""><em>Seçiniz</em></MenuItem>
                {salespersonsSerkanCanta.map(sp => (
                  <MenuItem key={sp.salespersonCode} value={sp.salespersonCode}>
                    {sp.salespersonCode} - {sp.salespersonFirstLastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>
      {/* Not: Ortak özellikler yukarıda; burada ekstra shared render yok */}
        
        {saveSuccess && (
          <Box mt={2}>
            <Alert severity="success">
              {customerCode === "temp" ? "Özellikler geçici olarak kaydedildi." : "Özellikler başarıyla kaydedildi."}
            </Alert>
          </Box>
        )}
        
        {saveError && (
          <Box mt={2}>
            <Alert severity="error">
              Özellikler kaydedilirken bir hata oluştu: {saveError}
            </Alert>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          İptal
        </Button>
        <Button 
          onClick={handleSave} 
          color="primary" 
          variant="contained"
          disabled={saving || !(() => {
            const init = initialSelectionsRef.current;
            if (!init) return false; // snapshot yoksa kayda izin verme (buton pasif)
            const isSame =
              init.st.price === selectedPriceGroupSt &&
              init.st.disc === selectedDiscountGroupSt &&
              init.st.inv === selectedInvoiceTypeSt &&
              init.st.sp === selectedSalespersonStDeri &&
              init.sc.price === selectedPriceGroupSc &&
              init.sc.disc === selectedDiscountGroupSc &&
              init.sc.inv === selectedInvoiceTypeSc &&
              init.sc.sp === selectedSalespersonSerkanCanta;
            return !isSame; // farklı ise aktif
          })()}
        >
          {saving ? <CircularProgress size={24} /> : "Kaydet"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerPropertiesModal;
